import {
  Banner,
  AlertIcon,
  Flex,
  InfoIcon,
  Typography,
  useToast,
} from '@iheartjane/reefer';
import { useIsMutating } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router';

import {
  useFetchCurrentBloomMenu,
  useUpdateBloomMenu,
} from '@jane/business-admin/data-access';
import { businessPaths } from '@jane/business-admin/util';

import { ImageUploader } from './components/ImageUploader';
import { ModalPageWrapper } from './components/ModalPageWrapper';

export const PremiumLogo = () => {
  const [imageErrors, setImageErrors] = useState<(string | boolean)[]>();
  const toast = useToast();
  const navigate = useNavigate();

  const bloomMenuQuery = useFetchCurrentBloomMenu();
  const { mutate: updateBloomMenu, isPending } = useUpdateBloomMenu(
    bloomMenuQuery.data?.id
  );

  const isUploading = useIsMutating({
    mutationKey: ['update-bloom-menu', bloomMenuQuery.data?.id],
  });

  const formMethods = useForm({
    defaultValues: {
      logo: bloomMenuQuery.data?.logo?.large ?? '',
    },
  });
  const { control, setValue, reset } = formMethods;
  const imageUrl = useWatch({ control, name: 'logo' });

  useEffect(() => {
    if (
      bloomMenuQuery.data?.logo?.large &&
      !bloomMenuQuery.isFetching &&
      !imageUrl
    ) {
      reset({
        logo: bloomMenuQuery.data.logo.large,
      });
    }
  }, [
    bloomMenuQuery.data?.logo?.large,
    bloomMenuQuery.isFetching,
    imageUrl,
    reset,
  ]);

  const publish = useCallback(() => {
    updateBloomMenu(
      {
        logo: imageUrl,
      },
      {
        onError: () => {
          toast.add({
            label: 'An error occurred.',
            variant: 'error',
          });
        },
        onSuccess: () => {
          toast.add({
            label: 'Menu logo updated!',
            variant: 'success',
          });
          navigate(businessPaths.premium());
        },
      }
    );
  }, [imageUrl, toast, updateBloomMenu, navigate]);

  return (
    <ModalPageWrapper
      formMethods={formMethods}
      isLoading={isPending || !!isUploading}
      onSubmit={publish}
      title="Logo"
    >
      <Flex
        p={40}
        gap={24}
        flexDirection="column"
        alignItems="center"
        alignContent="center"
      >
        <Typography width="100%">
          Upload your logo that will appear in the header of your homepage.
        </Typography>

        {imageErrors?.map((error, i) => (
          <Banner
            full
            icon={<AlertIcon />}
            label={error}
            variant="error"
            onDismiss={() =>
              setImageErrors(imageErrors.filter((e) => e !== error))
            }
            key={i}
          />
        ))}

        <ImageUploader
          height={264}
          width={264}
          imageUrl={imageUrl}
          isLoading={bloomMenuQuery.isPending || bloomMenuQuery.isFetching}
          onError={setImageErrors}
          onUpdateImage={(imageUrl: string) =>
            setValue('logo', imageUrl, { shouldDirty: true })
          }
        />

        {!imageUrl && (
          <Flex alignItems="center" gap={8}>
            <InfoIcon color="grays-mid" size="sm" />
            <Typography variant="small-body-regular" color="grays-mid">
              File must be a JPG or PNG under 2MB
            </Typography>
          </Flex>
        )}
      </Flex>
    </ModalPageWrapper>
  );
};
